import type { ReactElement } from 'react';
import { Link } from 'react-router';

type Side = 'mine' | 'theirs';

/**
 * Two balances per row, always the same two, always pressable. A press lands
 * on the page already filtered to that side; the row itself lands unfiltered.
 */
export function Balances({
  iOwe,
  theyOwe,
  to,
  side = null,
  onPick,
  them = 'owes you',
}: {
  iOwe: number;
  theyOwe: number;
  /** Where a press lands; `side=` is appended. */
  to?: string;
  side?: Side | null;
  /** In place of a link: the page filters itself. */
  onPick?: (side: Side | null) => void;
  them?: string;
}): ReactElement {
  const one = (key: Side, label: string, n: number): ReactElement => {
    const on = side === key;
    const cls = `balance ${on ? 'on' : ''}`;
    const body = (
      <>
        <span className="k">{label}</span>
        <span className="n">{n}</span>
      </>
    );
    if (onPick)
      return (
        <button
          type="button"
          className={cls}
          aria-pressed={on}
          onClick={(e) => {
            e.stopPropagation();
            onPick(on ? null : key);
          }}
        >
          {body}
        </button>
      );
    if (to)
      return (
        <Link
          to={`${to}${to.includes('?') ? '&' : '?'}side=${key}`}
          className={cls}
          onClick={(e) => e.stopPropagation()}
        >
          {body}
        </Link>
      );
    return <span className={cls}>{body}</span>;
  };
  return (
    <span className="balances">
      {one('mine', 'you owe', iOwe)}
      {one('theirs', them, theyOwe)}
    </span>
  );
}

/**
 * The same two numbers as words, for a row that is itself the link — a figure
 * in a box reads as a button, and a second button inside a row that opens is
 * one too many. Vladimir, 2026-09-24: "i dont like buttons who owns how many,
 * its shall not be buttons".
 */
export function BalanceText({
  iOwe,
  theyOwe,
  them = 'owes you',
}: {
  iOwe: number;
  theyOwe: number;
  them?: string;
}): ReactElement {
  if (iOwe === 0 && theyOwe === 0) return <span className="mono">nothing open</span>;
  return (
    <span className="mono" style={{ whiteSpace: 'nowrap' }}>
      {iOwe ? (
        <>
          you owe <b>{iOwe}</b>
        </>
      ) : null}
      {iOwe && theyOwe ? ' · ' : null}
      {theyOwe ? (
        <>
          {them} <b>{theyOwe}</b>
        </>
      ) : null}
    </span>
  );
}
